import * as GC from '@grapecity/spread-sheets';
import { isEmpty, isNumber } from '@helpers/arrays';
import { fromDateToExcelSerialDate } from '@helpers/from-date-to-excel-serial-date';
import { EmpyreanComment } from '@models/EmpyreanComment';
import { spreadsheetConfig } from '../../spreadsheet/spreadsheet-config';
import { EllipsisCellType } from './cell-types/ellipsis-cell-type';
import { outputReportNumberCellType } from './cell-types/output-report-number';
import { copyTextToClipboard } from './copy';

const COMMENTS = {
  backColor: 'yellow',
  textColor: 'green',
  cellBackColor: 'pink',
};

class CellTypeStyle {
  static discColor: string;
  static typeName: string;
}

export const centerAndWrapCell = (
  spread: GC.Spread.Sheets.Workbook,
  workSheet: GC.Spread.Sheets.Worksheet,
  row: number,
  column: number
) => {
  // spread.commandManager().execute({
  //   cmd: 'autoFitColumn',
  //   sheetName: workSheet.name,
  //   columns: column,
  //   isColumnHeader: false,
  //   autoFitType: GC.Spread.Sheets.AutoFitType.cell
  // });
  workSheet.getCell(row, column).wordWrap(true);
  workSheet.getCell(row, column).hAlign(GC.Spread.Sheets.HorizontalAlign.center);
};
export const autoFitCell = (spread: GC.Spread.Sheets.Workbook, workSheet: GC.Spread.Sheets.Worksheet, row: number, column: number) => {
  spread.commandManager().execute({
    cmd: 'autoFitColumn',
    sheetName: workSheet.name,
    columns: column,
    isColumnHeader: false,
    autoFitType: GC.Spread.Sheets.AutoFitType.cell,
  });
  // workSheet.getCell(row, column).wordWrap(true);
  // workSheet.getCell(row, column).hAlign(GC.Spread.Sheets.HorizontalAlign.center);
};

export const addRange = (type: 'row' | 'column', workSheet: GC.Spread.Sheets.Worksheet, start: number, count: number, expanded = true) => {
  // Rows
  if (type === 'row') {
    workSheet.rowOutlines.group(start, count);
    workSheet.rowOutlines.expand(0, expanded);
    if (!expanded) workSheet.rowOutlines.setCollapsed(start, true);
  }

  // Columns
  if (type === 'column') {
    workSheet.columnOutlines.group(start, count);
    workSheet.columnOutlines.expand(0, expanded);
    if (!expanded) workSheet.columnOutlines.setCollapsed(start, true);
  }

  // console.log(`${workSheet.name()}: Range applied to ${type}s from ${start} to ${start + count}`);
};

/**
 * @deprecated Use addRange('row', ... ) instead
 */
export const addRowRange = (workSheet: GC.Spread.Sheets.Worksheet, startRow: number, count: number) => {
  // TODO: Use addRange('row', ...) instead
  workSheet.rowOutlines.group(startRow, count);
  // workSheet.rowOutlines.setCollapsed(startRow,true);
  workSheet.rowOutlines.expand(0, false);
  workSheet.rowOutlines.direction(GC.Spread.Sheets.Outlines.OutlineDirection.backward);
  // console.log(`${workSheet.name()}: Range applied from ${startRow} to ${startRow + count}`);
};

export const addComment = (workSheet: GC.Spread.Sheets.Worksheet, row: number, col: number, message: string) => {
  const comment = new EmpyreanComment();
  comment.text(message);
  comment.backColor(COMMENTS.backColor);
  comment.foreColor(COMMENTS.textColor);
  comment.displayMode(GC.Spread.Sheets.Comments.DisplayMode.hoverShown);
  comment.autoSize(false);
  comment.row = row;
  comment.col = col;
  workSheet.getCell(row, col).comment(comment);
  // Removed the cell's backColor for Errors with comments
  // workSheet.getCell(row, col).backColor(COMMENTS.cellBackColor);
};

export const removeComment = (sheet: GC.Spread.Sheets.Worksheet, row: number, col: number) => {
  sheet.comments.remove(row, col);
  const cell = sheet.getCell(row, col);
  cell.backColor(null);
};

export const removeRowRange = (workSheet: GC.Spread.Sheets.Worksheet) => {
  workSheet.rowOutlines.ungroup();
};

/**
 * Checks if cell contains a valid item from the list validator (case insensitive) and if so, replaces it with the value on the list
 * (e.g. 'Generic' -> 'GENERIC')
 * @returns Final value of the cell
 */
export const checkDropdownValues = (
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number,
  newValue: any = null,
  listValidator: GC.Spread.Sheets.DataValidation.DefaultDataValidator = null,
  list?: string[]
): any => {
  let value = newValue === null ? sheet.getCell(row, col).value() : newValue;

  if (!isNumber(value) && !isEmpty(value)) {
    const validator = listValidator === null ? sheet.getDataValidator(row, col, GC.Spread.Sheets.SheetArea.viewport) : listValidator;
    let validatorList = null;

    if (validator) {
      validatorList = validator.getValidList(sheet, row, col);
    } else if (list) {
      validatorList = list;
    }
    if (validatorList) {
      const ValidatorListLowercase = validatorList.map(v => v.toString().toLowerCase());
      const index = ValidatorListLowercase.indexOf(value.toString().toLowerCase());
      if (index >= 0) {
        value = validatorList[index];
        sheet.getCell(row, col).value(value);
      }
    }
  }
  return value;
};

export const getListValidatorFromArray = (spread: GC.Spread.Sheets.Workbook, data: any[]) => {
  // saving validation list values in a hidden sheet
  spread.addSheet(spread.getSheetCount());
  const sheet = spread.getSheet(spread.getSheetCount() - 1);
  sheet.visible(false);
  for (let i = 0; i < data.length; i++) {
    sheet.setValue(i, 0, data[i]);
  }
  // create validator based on the values
  const dv = GC.Spread.Sheets.DataValidation.createFormulaListValidator(
    '=' + sheet.name() + '!$A$1:' + sheet.name() + '!$A$' + data.length
  );
  return dv;
};

export const applyEllipsisToCell = (cell: GC.Spread.Sheets.CellRange, maxWidth: number): void => {
  // save copy of the formatted text
  const formattedText = cell.text();
  // Apply the Ellipsis
  cell.cellType(EllipsisCellType(maxWidth)); // It transforms the value of paint
  // Recover the formatted text over which will be applied the ellipsis
  cell.value(formattedText);
  cell.wordWrap(false);
};

// Applies a standard format to Numbers:
// -- Negative numbers are displayed red with parenthesis and without the minus sign.
// -- However his value should be numeric when is exported to excel and should not have parenthesis
// -- Positive numbers are black
export const setOutputReportNumber = (
  spread: GC.Spread.Sheets.Workbook,
  sheet: GC.Spread.Sheets.Worksheet,
  cell: GC.Spread.Sheets.CellRange,
  numericValue: number,
  customFormatter: string = null,
  roundEnabled: boolean = false
): void => {
  // Altering the order of the next lines will affect the final output/result.

  // Parenthesis + preservation of real value
  cell.value(numericValue);
  // Format it
  const currencySymbol = '';
  setAccountingFormatting(cell, currencySymbol, customFormatter, roundEnabled);

  // Get the display text formatted
  const displayText = cell.hasOwnProperty('text') ? cell.text() : cell.value();
  // Set that display text necessary to avoid hashing out the values
  cell.cellType(outputReportNumberCellType(displayText));
  // Reassignment of the real value
  cell.value(numericValue);

  // Right alignment after autofit
  autoFitCell(spread, sheet, cell.row, cell.col);
  cell.hAlign(GC.Spread.Sheets.HorizontalAlign.right);
};

// Applies a standard format to Dates
export const setOutputReportDate = (
  spread: GC.Spread.Sheets.Workbook,
  sheet: GC.Spread.Sheets.Worksheet,
  cell: GC.Spread.Sheets.CellRange,
  date: string | number | Date,
  isAsOfDate: boolean = false,
  customFormatter: string = null
): void => {
  date = isAsOfDate ? (fromDateToExcelSerialDate(date as Date) as number) : (date as string);
  cell.value(date);

  // Date Formatting
  const formatter = customFormatter || spreadsheetConfig.formatters.getDateFormatter(isAsOfDate);
  cell.formatter(formatter);

  // Styling
  cell.font(spreadsheetConfig.fonts.getFont('sm', true));
  autoFitCell(spread, sheet, cell.row, cell.col);
  cell.hAlign(GC.Spread.Sheets.HorizontalAlign.center);
  cell.vAlign(GC.Spread.Sheets.VerticalAlign.center);
  if (!isAsOfDate) cell.textDecoration(GC.Spread.Sheets.TextDecorationType.underline);
};

export const setOutputReportPeriodNumber = (cell: GC.Spread.Sheets.CellRange, periodNum: any): void => {
  cell.textDecoration(GC.Spread.Sheets.TextDecorationType.underline);
  cell.value(periodNum);
};

// Applies accounting formating to a cell
// -- Docs: http://help.grapecity.com/spread/SpreadSheets11/webframe.html#accounting.html
export const setAccountingFormatting = (
  cell: GC.Spread.Sheets.CellRange,
  currencySymbol = '',
  customFormatter: string = null,
  roundEnabled: boolean = false
) => {
  const format = spreadsheetConfig.formatters.getAccountingFormatter(currencySymbol, customFormatter, roundEnabled);
  cell.formatter(format);
};

export const getColumnHeaderName = (sheet: GC.Spread.Sheets.Worksheet, colIndex: number): string => {
  return sheet.getValue(0, colIndex, GC.Spread.Sheets.SheetArea.colHeader);
};

export const getColumnNames = (sheet: GC.Spread.Sheets.Worksheet): string[] => {
  const columnNames = [];
  for (let i = 0; i < sheet.getColumnCount(); i++) {
    const colName = sheet.getDataColumnName(i);
    columnNames.push(colName);
  }
  return columnNames;
};

export const allowPastingRowsBeyond = (sheet: GC.Spread.Sheets.Worksheet, info) => {
  if (!sheet || !info) {
    console.warn('Invalid parameters provided to allowPastingRowsBeyond');
    return;
  }

  try {
    // Handle both direct paste and clipboard paste scenarios
    // For clipboard paste, the data is in info.pasteData
    // For drag operations, the data is in info.cellRange
    const cellRange = info.cellRange || (info.pasteData && { 
      row: info.row || 0,
      rowCount: info.pasteData.text ? info.pasteData.text.split('\n').length : 1
    });

    if (!cellRange) {
      console.warn('Could not determine paste range in allowPastingRowsBeyond');
      return;
    }

    const numRows = sheet.getRowCount();
    // Ensure row is a positive number
    const safeRow = cellRange.row >= 0 ? cellRange.row : 0;
    
    // For clipboard data, calculate rowCount from the clipboard text
    let rowCount = cellRange.rowCount;
    if (info.pasteData && info.pasteData.text) {
      // Count rows by splitting on newlines
      const lines = info.pasteData.text.trim().split('\n');
      rowCount = lines.length;
    }
    
    // Calculate how many additional rows are needed
    const pastingExcess = safeRow + rowCount - numRows;
    
    if (pastingExcess > 0) {
      console.log(`Adding ${pastingExcess} rows to accommodate paste operation with ${rowCount} rows of data`);
      
      // Add rows to accommodate the paste data
      // Add to the end of the sheet, not to getRowCount() - 1 which might skip the last row
      sheet.addRows(sheet.getRowCount(), pastingExcess);
      console.log(`Sheet now has ${sheet.getRowCount()} rows after expansion`);
    }
  } catch (error) {
    console.error('Error in allowPastingRowsBeyond:', error);
    // Don't throw - we want to allow paste to continue even if row expansion fails
  }
};

// Locks the columns beyond the current columns count of the sheet
export const preventPastingColumnsBeyond = (sheet: GC.Spread.Sheets.Worksheet, event, info) => {
  if (!sheet || !info) {
    console.warn('Invalid parameters provided to preventPastingColumnsBeyond');
    return false;
  }

  try {
    // Get the maximum allowed columns in the sheet
    const maxColumns = getColumnNames(sheet).length;
    console.log(`Sheet has ${maxColumns} columns available`);

    // Handle both direct paste and clipboard paste scenarios
    // For clipboard paste, calculate column count from the pasteData
    let startCol = 0;
    let colCount = 1;

    if (info.cellRange) {
      // Direct paste from drag or existing sheet operation
      startCol = info.cellRange.col;
      colCount = info.cellRange.colCount;
    } else if (info.pasteData && info.pasteData.text) {
      // Clipboard paste operation
      startCol = info.row !== undefined && info.col !== undefined ? info.col : 
                  (sheet.getActiveRowIndex() >= 0 ? sheet.getActiveColumnIndex() : 0);
      
      // Calculate max columns in clipboard data by finding the line with most tabs
      const lines = info.pasteData.text.trim().split('\n');
      if (lines.length > 0) {
        // Count columns accounting for tab delimiters
        const columnCounts = lines.map(line => {
          // Tab count + 1 gives us the number of columns
          return line.split('\t').length;
        });
        colCount = Math.max(...columnCounts);
        console.log(`Clipboard data contains ${colCount} columns from ${lines.length} rows`);
      }
    }

    // Calculate if paste would exceed columns
    const pastingExcess = startCol + colCount - maxColumns;

    // Prevent pasting if it would exceed the available columns
    if (pastingExcess > 0) {
      console.log(`[Pasting prevented] Trying to paste ${colCount} columns starting at column ${startCol}, which exceeds the ${maxColumns} available columns by ${pastingExcess}`);
      
      // Prevent the default event using all available methods
      if (event) {
        if (typeof event.preventDefault === 'function') {
          event.preventDefault();
        }
        
        if (typeof event.stopPropagation === 'function') {
          event.stopPropagation();
        }
        
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
      }
      
      // Create a read-only barrier in the destination area to block the paste
      try {
        // Temporarily lock the target area to prevent paste
        const originalLockState = [];
        for (let c = startCol; c < startCol + colCount; c++) {
          if (c < maxColumns) {
            // Store original lock state
            const cellRange = sheet.getRange(0, c, sheet.getRowCount(), 1);
            originalLockState.push(cellRange.locked());
            // Lock the column to prevent paste
            cellRange.locked(true);
          }
        }
        
        // Set sheet to protected mode temporarily to enforce locks
        const wasProtected = sheet.options.isProtected;
        sheet.options.isProtected = true;
        
        // Schedule unlock after a short delay to restore normal behavior
        setTimeout(() => {
          // Restore original protection state
          sheet.options.isProtected = wasProtected;
          
          // Restore original lock state
          for (let c = startCol, i = 0; c < startCol + colCount && i < originalLockState.length; c++, i++) {
            if (c < maxColumns) {
              const cellRange = sheet.getRange(0, c, sheet.getRowCount(), 1);
              cellRange.locked(originalLockState[i]);
            }
          }
        }, 100);
      } catch (e) {
        console.warn('Error setting temporary protection:', e);
      }
      
      return true; // Return true to indicate paste was prevented
    }
    
    console.log(`Paste operation allowed: ${colCount} columns at column ${startCol} within ${maxColumns} available columns`);
    return false; // Paste not prevented
  } catch (error) {
    console.error('Error in preventPastingColumnsBeyond:', error);
    return false; // Don't prevent paste on error, let default behavior work
  }
};

export const setOutputReportNumberFormatting = (
  spread: GC.Spread.Sheets.Workbook,
  workSheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number,
  val: any,
  customFormatter: string = null,
  roundEnabled: boolean = false
) => {
  const numericValue = Number(val);
  const cell = workSheet.getCell(row, col);
  workSheet.setValue(row, col, numericValue);
  setOutputReportNumber(spread, workSheet, cell, numericValue, customFormatter, roundEnabled);
  // font overwrite (affects all the result pages)
  cell.font(spreadsheetConfig.fonts.getFont('x-sm'));
};

export const copyValues = (context: GC.Spread.Sheets.Workbook, options, isUndo) => {
  console.log(`copyValues`, { context, options, isUndo });

  let text = '';
  const sheet: GC.Spread.Sheets.Worksheet = context.getActiveSheet();
  const selection = sheet.getSelections()[0];
  if (selection.row === -1) {
    // Header area selected
    selection.row = 0;
  }

  if (selection.col === -1) {
    selection.col = 0;
  }

  const data = sheet.getArray(selection.row, selection.col, selection.rowCount, selection.colCount);
  // console.log(data);
  data.forEach((entireRow, rowIndex) => {
    entireRow.forEach((col, colIndex) => {
      if (col === undefined || col === null) {
        col = '';
      }
      text += col + (colIndex + 1 < selection.colCount ? '\t' : '');
    });
    if (rowIndex + 1 < selection.rowCount) {
      text += '\n';
    }
  });
  return copyTextToClipboard(text);
};
